import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'

export interface AuthState {
  // 用户状态
  user: {
    id: string
    email: string
  } | null
  session: {
    access_token: string
    refresh_token: string
    expires_at: number
  } | null
  isLoading: boolean
  error: string | null

  // 登录流程状态
  loginStep: 'email' | 'otp' | 'success'
  tempEmail: string
  isLoginPanelOpen: boolean

  // 操作
  setLoginStep: (step: 'email' | 'otp' | 'success') => void
  setTempEmail: (email: string) => void
  openLoginPanel: () => void
  closeLoginPanel: () => void
  sendOTP: (email: string) => Promise<boolean>
  verifyOTP: (email: string, otp: string) => Promise<boolean>
  logout: () => Promise<void>
  clearError: () => void
  restoreSession: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 初始状态
      user: null,
      session: null,
      isLoading: false,
      error: null,
      loginStep: 'email',
      tempEmail: '',
      isLoginPanelOpen: false,

      // 设置登录步骤
      setLoginStep: (step) => set({ loginStep: step }),

      // 设置临时邮箱
      setTempEmail: (email) => set({ tempEmail: email }),

      // 打开登录面板
      openLoginPanel: () => set({ isLoginPanelOpen: true, loginStep: 'email', error: null }),

      // 关闭登录面板
      closeLoginPanel: () => set({ isLoginPanelOpen: false }),

      // 发送 OTP
      sendOTP: async (email) => {
        set({ isLoading: true, error: null })

        try {
          const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
              shouldCreateUser: true,
              // 明确指定使用 OTP 而不是魔法链接
              emailRedirectTo: undefined,
            },
          })

          if (error) {
            set({ error: error.message, isLoading: false })
            return false
          }

          set({ tempEmail: email, loginStep: 'otp', isLoading: false })
          return true
        } catch (err) {
          set({ error: '发送验证码失败，请稍后重试', isLoading: false })
          return false
        }
      },

      // 验证 OTP
      verifyOTP: async (email, otp) => {
        set({ isLoading: true, error: null })

        try {
          const { data, error } = await supabase.auth.verifyOtp({
            email,
            token: otp,
            type: 'email',
          })

          if (error) {
            set({ error: error.message, isLoading: false })
            return false
          }

          if (data.user && data.session) {
            set({
              user: {
                id: data.user.id,
                email: data.user.email!,
              },
              session: {
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token,
                expires_at: data.session.expires_at!,
              },
              loginStep: 'success',
              isLoading: false,
              error: null,
            })
            return true
          }

          set({ error: '登录失败，请重试', isLoading: false })
          return false
        } catch (err) {
          set({ error: '验证失败，请稍后重试', isLoading: false })
          return false
        }
      },

      // 退出登录
      logout: async () => {
        set({ isLoading: true })

        try {
          await supabase.auth.signOut()
          set({
            user: null,
            session: null,
            loginStep: 'email',
            tempEmail: '',
            isLoading: false,
            error: null,
          })
        } catch (err) {
          set({ isLoading: false })
        }
      },

      // 清除错误
      clearError: () => set({ error: null }),

      // 恢复会话
      restoreSession: async () => {
        const { session: storedSession } = get()

        if (!storedSession) return false

        // 检查会话是否过期
        if (storedSession.expires_at * 1000 < Date.now()) {
          // 尝试刷新会话
          try {
            const { data, error } = await supabase.auth.refreshSession({
              refresh_token: storedSession.refresh_token,
            })

            if (error || !data.session || !data.user) {
              set({ session: null, user: null })
              return false
            }

            set({
              user: {
                id: data.user.id,
                email: data.user.email!,
              },
              session: {
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token,
                expires_at: data.session.expires_at!,
              },
            })
            return true
          } catch {
            set({ session: null, user: null })
            return false
          }
        }

        // 会话有效，设置到 supabase 客户端
        await supabase.auth.setSession({
          access_token: storedSession.access_token,
          refresh_token: storedSession.refresh_token,
        })

        // 获取当前用户
        const { data: { user } } = await supabase.auth.getUser()

        if (user) {
          set({
            user: {
              id: user.id,
              email: user.email!,
            },
          })
          return true
        }

        return false
      },
    }),
    {
      name: 'huaxia-chronicles-auth-storage',
      partialize: (state) => ({
        user: state.user,
        session: state.session,
      }),
    }
  )
)
